import { useSuspenseQuery } from '@tanstack/react-query';
import qs from 'qs';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import { appendDateTimestamp } from '@/utils/appendDateTimestamp';
import { createTakerMarketUuidsMap } from '@/utils/takerMarketUuidMap';
import { TakerMarket } from './useTakerMarkets';

export type ClearingInvoiceRequestBody =
  paths['/clearing-invoices']['post']['requestBody']['content']['application/json'];
export type ClearingInvoiceResponse =
  paths['/clearing-invoices']['post']['responses']['200']['content']['application/json'];
export type ClearingInvoiceFetchOptions = paths['/clearing-invoices']['post']['parameters']['query'];
export type ClearingInvoiceSearchFilters = ClearingInvoiceRequestBody['search'];
export type ClearingInvoice = ClearingInvoiceResponse['data']['invoices'][number];

interface ClearingInvoiceParams {
  makerOrganizationUuid: string;
  options?: ClearingInvoiceFetchOptions;
  search?: ClearingInvoiceSearchFilters;
  takerMarkets: TakerMarket[];
}

const fetchClearingInvoices = async ({
  makerOrganizationUuid,
  options,
  search,
  takerMarkets,
}: ClearingInvoiceParams): Promise<ClearingInvoiceResponse['data']> => {
  const limit = options?.limit ?? 100;
  const params = qs.stringify({ ...options, limit }, { addQueryPrefix: true });

  const response = await apiClient
    .post(`api/supplier-experience/clearing-invoices${params}`, {
      json: {
        makerOrganizationUuid,
        supplierMarkets: takerMarkets.map((takerMarket) => ({
          marketUuid: takerMarket.marketUuid,
          supplierDivisionUuid: takerMarket.takerDivisionUuid,
        })),
        ...(search ? { search } : {}),
      } satisfies ClearingInvoiceRequestBody,
    })
    .json<ClearingInvoiceResponse>();

  return {
    ...response.data,
    invoices: response.data.invoices.map((invoice) => ({
      ...invoice,
      dueDate: appendDateTimestamp(invoice.dueDate) || invoice.dueDate,
      newPayDate: appendDateTimestamp(invoice.newPayDate) || invoice.newPayDate,
      clearDate: invoice.clearDate ? appendDateTimestamp(invoice.clearDate) || undefined : undefined,
      scheduledClearDate: invoice.scheduledClearDate
        ? appendDateTimestamp(invoice.scheduledClearDate) || undefined
        : undefined,
    })),
  };
};

const useClearingInvoices = (params: ClearingInvoiceParams) => {
  const { makerOrganizationUuid, options, search, takerMarkets } = params;
  const isValid = !!makerOrganizationUuid && !!takerMarkets.length;
  const takerMarketUuidsMap = createTakerMarketUuidsMap(takerMarkets).sort();

  return useSuspenseQuery({
    queryKey: isValid
      ? ['clearing-invoices', makerOrganizationUuid, options, search, ...takerMarketUuidsMap]
      : ['clearing-invoices-invalid'],
    queryFn: () => (isValid ? fetchClearingInvoices(params) : null),
  });
};

export default useClearingInvoices;
